import { FaberApi } from 'api'
import { AxiosPromise } from 'axios'
import {
  GetSubscriptions,
  GetSubscriptionTotals,
  ICustomerSubHistory,
  IOrderId,
  ISimServiceStatus,
  IUpgradeNow,
  UpdateCustomerRequest
} from 'types/api/faber/subscription.type'
import { UpdateUserDetailsResponse } from 'types/api/faber/subscription.type'

class SubscriptionsAPI {
  /**
   * to get all the subscription details
   * @returns
   */
  static getAllSubscriptions(
    faberToken: string,
    limit = 10,
    offset = 1,
    statusFilters: string[],
    bundleFilters: string[],
    searchCriteria: string,
    searchValue: string,
    sortDate: string,
    startAt: string,
    endAt: string
  ): AxiosPromise<GetSubscriptions> {
    /* REPLACE TERMS WITH EQUIVALENT USED ON BE */
    const status =
      statusFilters.length > 0
        ? '&statuses=' +
          statusFilters
            .map((value) => {
              if (value === 'PENDING UNSUBSCRIPTION') {
                return 'EXPIRING'
              } else if (value === 'PENDING ACTIVATION') {
                return 'PENDING'
              }
              return value
            })
            .join(',')
        : ''

    const plan =
      bundleFilters.length > 0 ? '&bundleNames=' + bundleFilters.join(',') : ''

    let email = ''
    let msisdn = ''
    let iccid = ''
    let userid = ''

    switch (searchCriteria) {
      case 'Email':
        email = searchValue.length > 0 ? `&email=${searchValue}` : ''
        break
      case 'MSISDN':
        msisdn = searchValue.length > 0 ? `&msisdns=${searchValue}` : ''
        break
      case 'ICCID':
        iccid = searchValue.length > 0 ? `&iccid=${searchValue}` : ''
        break
      case 'Customer ID':
        userid = searchValue.length > 0 ? `&userID=${searchValue}` : ''
    }

    const orderBy = `&orderBy=${sortDate}`

    const dateRange = `&startAt=${startAt}&endAt=${endAt}`

    return FaberApi.httpGet(
      `/subscriptions?limit=${limit}&offset=${offset}${status}${plan}${email}${msisdn}${iccid}${userid}${orderBy}${dateRange}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  /**
   * to get the subscription totals
   * @returns
   */
  static getSubscriptionTotals(
    faberToken: string
  ): AxiosPromise<GetSubscriptionTotals> {
    return FaberApi.httpGet(
      `/subscriptions/active`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  /**
   * to suspend user subscription
   */
  static suspendUserSubscription(body: { userID: string; faberToken: string }) {
    return FaberApi.httpPut(
      `/subscriptions/suspend`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  /**
   * to unsuspend user subscription
   */

  static unsuspendUserSubscription(body: {
    userID: string
    faberToken: string
  }) {
    return FaberApi.httpPut(
      `/subscriptions/unsuspend`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  /**
   * to update details for one customer
   * @returns
   */
  static updateCustomerDetails(body: UpdateCustomerRequest) {
    return FaberApi.httpPost(
      `/subscriptions/detail`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  /**
   * to upgrade or downgrade user subscription plan
   * @returns
   */
  static updateCustomerSubscriptionPlan(body: {
    customerID: string
    newBundleID: string
    faberToken: string
  }) {
    return FaberApi.httpPut(
      `/subscriptions/subtier/update`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      {
        customerID: body.customerID,
        newBundleID: body.newBundleID
      }
    )
  }

  /**
   * to update faber user details
   */

  static updateFaberUserDetails(body: {
    userId: string
    email: string
    firstName: string
    lastName: string
    remarks: string
    faberToken: string
  }): AxiosPromise<UpdateUserDetailsResponse> {
    return FaberApi.httpPut(
      '/subscriptions/user/info',
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }
  /**
   *
   * @param faberToken
   * @param uploadObject uploaded CSV
   */

  static uploadTerminatedSins(body: {
    faberToken: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uploadObject: any
  }): AxiosPromise {
    return FaberApi.httpPost(
      `/telco/user/terminatedsins`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body.uploadObject
    )
  }

  static cancelSubscriptionImmediately(body: {
    faberToken: string
    UserID: string
  }): AxiosPromise {
    return FaberApi.httpPost(
      `/subscriptions/cancel-now`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  static getCustomerSubscriptionHistory(body: {
    faberToken: string
    user_id: string
  }): AxiosPromise<ICustomerSubHistory> {
    return FaberApi.httpPost(
      `/subscription/all`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  static getSimServiceStatus(body: {
    faberToken: string
    userID: string
  }): AxiosPromise<ISimServiceStatus> {
    return FaberApi.httpGet(
      `/esim/service/status/${body.userID}`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      true
    )
  }

  static upgradeNow(body: {
    userId: string
    catalog_bundle_id: number
    type: string
    faberToken: string
  }): AxiosPromise<IUpgradeNow> {
    return FaberApi.httpPost(
      `/order`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }
  static checkOrderId(body: {
    orderID: string
    faberToken: string
  }): AxiosPromise<IOrderId> {
    return FaberApi.httpGet(
      `/order/${body.orderID}`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      true
    )
  }
}

export default SubscriptionsAPI
